import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/ui/page-hero";
import { heroImages } from "@/lib/image-map";
import { Mail, MapPin, Phone } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ui/scroll-reveal";
import { pageSchema } from "@/lib/organization";

export default function Contact() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.target as HTMLFormElement;
    const data = {
      name: (form.elements.namedItem("name") as HTMLInputElement).value,
      phone: (form.elements.namedItem("phone") as HTMLInputElement).value,
      email: (form.elements.namedItem("email") as HTMLInputElement).value,
      town: (form.elements.namedItem("town") as HTMLSelectElement).value,
      service: (form.elements.namedItem("service") as HTMLSelectElement).value,
      description: (form.elements.namedItem("description") as HTMLTextAreaElement).value,
    };

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        throw new Error("Server error");
      }

      const result = await res.json() as { success: boolean; autoReplySent: boolean };

      if (data.email && result.autoReplySent === false) {
        toast({
          title: "Request Received",
          description: "We got your message. We'll get back to you within 1 business day to schedule a walkthrough. Note: your confirmation email may not have arrived. Please check your spam folder or call us directly at 508-505-1539.",
        });
      } else {
        toast({
          title: "Request Received",
          description: "Thank you for reaching out! We'll get back to you within 1 business day to schedule a walkthrough.",
        });
      }
      form.reset();
    } catch {
      toast({
        title: "Something went wrong",
        description: "We couldn't send your request. Please call us directly at 508-505-1539.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact & Free Estimates | TLC Associates LLC"
        description="Contact TLC Associates LLC for a landscaping estimate in Norfolk County by phone or through the online project request form."
        path="/contact"
        h1="Contact TLC Associates LLC"
        schema={pageSchema("/contact", "ContactPage", "Contact & Free Estimates | TLC Associates LLC", "Contact TLC Associates LLC for a landscaping estimate in Norfolk County by phone or through the online project request form.")}
      />

      <PageHero
        imageSrc={heroImages["/contact"]}
        eyebrow="Contact TLC Associates LLC"
        headline="Tell Us About Your Property"
        description="TLC Associates LLC will review your project details and follow up about a walkthrough and estimate."
      />

      <section className="py-20 bg-[#f7f3ed]">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            <ScrollReveal direction="left" className="order-2 lg:order-1">
              <div>
                <h2 className="text-3xl text-[#1a3a1a] mb-8">Get In Touch</h2>
                <p className="text-lg text-gray-700 mb-10">
                  We're ready to discuss your landscaping, renovation, or maintenance needs. Call us directly or fill out the form, and Toby will be in touch to discuss your vision.
                </p>

                <StaggerContainer className="space-y-8" staggerDelay={0.1}>
                  <StaggerItem direction="left">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm shrink-0 mt-1">
                        <Phone className="w-5 h-5 text-[#2a7a2a]" />
                      </div>
                      <div className="ml-6">
                        <h3 className="text-xl font-bold text-[#1a3a1a] mb-1">Phone</h3>
                        <p className="text-gray-600 mb-2">Call us for immediate assistance or to schedule an estimate.</p>
                        <a href="tel:5085051539" className="text-xl font-bold text-[#c84060] hover:underline">508-505-1539</a>
                      </div>
                    </div>
                  </StaggerItem>

                  <StaggerItem direction="left">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm shrink-0 mt-1">
                        <MapPin className="w-5 h-5 text-[#2a7a2a]" />
                      </div>
                      <div className="ml-6">
                        <h3 className="text-xl font-bold text-[#1a3a1a] mb-1">Address</h3>
                        <p className="text-gray-600 mb-2">TLC Associates LLC<br/>149 West Central Street<br/>Franklin, MA 02038</p>
                      </div>
                    </div>
                  </StaggerItem>
                  
                  <StaggerItem direction="left">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm shrink-0 mt-1">
                        <Mail className="w-5 h-5 text-[#2a7a2a]" />
                      </div>
                      <div className="ml-6">
                        <h3 className="text-xl font-bold text-[#1a3a1a] mb-1">Hours</h3>
                        <p className="text-gray-600 mb-2">Available 24/7 for emergency tree service and commercial snow removal. General inquiries responded to within 1 business day.</p>
                      </div>
                    </div>
                  </StaggerItem>
                </StaggerContainer>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="right" delay={0.15} className="order-1 lg:order-2">
              <div id="estimate-form" className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border border-gray-100">
                <h2 className="text-2xl text-[#1a3a1a] mb-6">Request Your Free Estimate</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                      <input 
                        type="text" 
                        id="name" 
                        name="name"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#2a7a2a] focus:border-transparent outline-none transition-shadow"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                      <input 
                        type="tel" 
                        id="phone" 
                        name="phone"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#2a7a2a] focus:border-transparent outline-none transition-shadow"
                        placeholder="Your phone number"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email (Optional)</label>
                      <input 
                        type="email" 
                        id="email" 
                        name="email"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#2a7a2a] focus:border-transparent outline-none transition-shadow"
                        placeholder="Your email address"
                      />
                    </div>
                    <div>
                      <label htmlFor="town" className="block text-sm font-medium text-gray-700 mb-2">Town</label>
                      <select 
                        id="town" 
                        name="town"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#2a7a2a] focus:border-transparent outline-none transition-shadow bg-white"
                      >
                        <option value="">Select your town</option>
                        <option value="Franklin">Franklin</option>
                        <option value="Bellingham">Bellingham</option>
                        <option value="Medway">Medway</option>
                        <option value="Norfolk">Norfolk</option>
                        <option value="Wrentham">Wrentham</option>
                        <option value="Other (Norfolk County)">Other (Norfolk County)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">Service Needed</label>
                    <select 
                      id="service" 
                      name="service"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#2a7a2a] focus:border-transparent outline-none transition-shadow bg-white"
                    >
                      <option value="">Select a service type</option>
                      <option value="Landscape Maintenance">Landscape Maintenance</option>
                      <option value="Lawn Care">Lawn Care</option>
                      <option value="Landscaping (Design & Renovation)">Landscaping (Design & Renovation)</option>
                      <option value="Garden & Planting">Garden & Planting</option>
                      <option value="Stonework & Hardscaping">Stonework & Hardscaping</option>
                      <option value="Tree & Shrub Care">Tree & Shrub Care</option>
                      <option value="Snow Plowing">Snow Plowing</option>
                      <option value="Commercial Landscaping">Commercial Landscaping</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">Project Description</label>
                    <textarea 
                      id="description" 
                      name="description"
                      rows={4}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#2a7a2a] focus:border-transparent outline-none transition-shadow resize-none"
                      placeholder="Tell us a bit about what you're looking for..."
                    ></textarea>
                  </div>

                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#c84060] hover:bg-[#b53553] text-white px-6 py-4 rounded-lg font-medium text-lg transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Sending Request..." : "Submit Your Estimate Request"}
                  </button>
                </form>
              </div>
            </ScrollReveal>

          </div>
        </div>
      </section>
    </>
  );
}
